import {ReactNode, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '@/contexts/AuthContext';
import {useToast} from '@/hooks/use-toast';
import {PageLoader} from '@/shared/ui/page-loader';
import {canAccessMasterInventory} from '@/shared/lib/master-inventory-access';

/**
 * Standalone Master Inventory — admins (when viewing as admin) and users who
 * are a project_manager on at least one project. Login role alone is not enough.
 */
const MasterInventoryGuard = ({children}: {children: ReactNode}) => {
  const {user, loading, effectiveRole} = useAuth();
  const navigate = useNavigate();
  const {toast} = useToast();

  const allowed = canAccessMasterInventory({
    effectiveRole,
    hasProjectManagerMembership: user?.hasProjectManagerMembership,
  });

  useEffect(() => {
    if (loading || !user || allowed) return;
    navigate('/', {replace: true});
    toast({
      title: 'Access Denied',
      description:
        'Master Inventory is available when you are a project manager on at least one project.',
      variant: 'destructive',
    });
  }, [allowed, loading, navigate, toast, user]);

  if (loading || !user) {
    return <PageLoader fullScreen />;
  }

  if (!allowed) {
    return <PageLoader fullScreen />;
  }

  return <>{children}</>;
};

export default MasterInventoryGuard;
